import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import CommentEl from "./Comment";
import CommentForm from "./CommentForm";
import { getThreadSelector } from "../../state/selectors/general.selectors";
import Topic from "../../models/Topic";
import Loader from "../Loader";
import SourceEntity from "../Publications/models/SourceEntity";
import CommentButton from "../Publications/components/SocialFeatures/CommentButton";
import LikeButton from "../Publications/components/SocialFeatures/LikeButton";
import threadService from "../../services/threadService";
import isPortal from "../../common/helpers/isPortal";

const scrollBehavior = {
  behavior: "smooth",
  block: "center",
  inline: "nearest",
};

const getThreadLineClass = (hideLine) => {
  return hideLine
    ? "tw-border-blueGray-300"
    : "tw-absolute tw--mt-6 left--px tw-top-0 tw-h-14 tw-w-3 tw-border-l tw-border-b tw-rounded-bl-2xl tw-border-blueGray-300";
};

const getContainerClass = (hideLine) => {
  return hideLine
    ? "tw-pt-3 tw-border-blueGray-300"
    : "tw-relative tw-pt-3 tw-ml-3 no-border-last tw-pl-3 tw-border-l tw-border-blueGray-300";
};

function Thread({
  sourceEntity,
  subType,
  subTypeId,
  slug,
  shouldAutoExpand,
  shouldHideExpandButton,
  shouldHideLines,
  topic,
}) {
  const isClinical = isPortal();
  const replyFormRef = useRef(null);
  const [isRepliesExpanded, setRepliesExpanded] = useState(shouldAutoExpand);
  const { type, id: typeId } = sourceEntity || {};
  const { comments } = topic;

  useEffect(() => {
    if (isRepliesExpanded) {
      threadService
        .loadTopicAndComments(type, typeId, subType, subTypeId)
        .subscribe();
    }
  }, [isRepliesExpanded]);

  function getCommentField() {
    return replyFormRef.current.getElementsByTagName("textarea")?.[0] || {};
  }

  const handleReply = (userHandle) => {
    replyFormRef.current.scrollIntoView(scrollBehavior);
    const el = getCommentField();
    const val = el?.value || "";
    el.value = `${val} ${userHandle} `;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  };

  return (
    <>
      {shouldHideExpandButton ? null : (
        <div className="tw-flex tw-text-blueGray-500 tw--mt-0.5 tw-items-center tw-text-2xs tw-px-6 tw-py-1 tw-gap-4 ">
          <LikeButton
            sourceEntity={sourceEntity}
            subType="classifications"
            subTypeId={subTypeId}
            slug={slug}
          />

          <div onClick={() => setRepliesExpanded(!isRepliesExpanded)}>
            <CommentButton
              sourceEntity={sourceEntity}
              isActive={false}
              slug={slug}
              className="tw-w-6 tw-h-6"
              size={16}
            />
          </div>
        </div>
      )}

      {isRepliesExpanded ? (
        <div>
          {isClinical ? null : (
            <div
              ref={replyFormRef}
              className={getContainerClass(shouldHideLines)}
            >
              <div className={getThreadLineClass(shouldHideLines)} />
              <div className="tw-relative">
                <CommentForm
                  topic={topic}
                  slug={slug}
                  className="tw-p-2"
                  sourceEntity={sourceEntity}
                  subType={subType}
                  subTypeId={subTypeId}
                />
              </div>
            </div>
          )}
          {isRepliesExpanded && !topic.isLoading ? (
            <>
              {comments.map((x) => (
                <div key={x.id} className={getContainerClass(shouldHideLines)}>
                  <div className={getThreadLineClass(shouldHideLines)} />
                  <CommentEl
                    topic={topic}
                    sourceEntity={sourceEntity}
                    subType={subType}
                    subTypeId={subTypeId ? String(subTypeId) : null}
                    handleReply={handleReply}
                    comment={x}
                    slug={slug}
                  />
                </div>
              ))}
            </>
          ) : null}
        </div>
      ) : null}

      {isRepliesExpanded && topic.isLoading ? (
        <div className="tw-pb-3">
          <Loader size="small" shouldHideText={true} />
        </div>
      ) : null}

      {isRepliesExpanded && !topic.isLoading && !comments.length ? (
        <div
          className={
            shouldHideLines
              ? "tw-text-blueGray-400 tw-pt-6"
              : "tw-pb-6 tw-pl-6 tw-pt-3 tw-text-blueGray-400"
          }
        >
          No Comments
        </div>
      ) : null}
    </>
  );
}

Thread.propTypes = {
  sourceEntity: PropTypes.instanceOf(SourceEntity),
  subType: PropTypes.string,
  subTypeId: PropTypes.string,
  slug: PropTypes.string,
  shouldAutoExpand: PropTypes.bool,
  shouldHideExpandButton: PropTypes.bool,
  shouldHideLines: PropTypes.bool,
  topic: PropTypes.instanceOf(Topic),
};

Thread.defaultProps = {
  sourceEntity: null,
  subType: "",
  subTypeId: "",
  slug: "",
  topic: new Topic({ slug: "", isLoading: true }),
  shouldAutoExpand: false,
  shouldHideExpandButton: false,
  shouldHideLines: true,
};

const mapStateToProps = (state, { slug }) => {
  return {
    topic: getThreadSelector(state, slug),
  };
};

export default React.memo(connect(mapStateToProps, {})(Thread));
